import React, { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { Button } from "../ui/button";
import { X, GripVertical } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useDraggable } from "@dnd-kit/core";
import ClaryNextInfoModal from "./modals/ClaryNextInfoModal";
import ActivityInfoModal from "./modals/ActivityInfoModal";
import SenderProfileModal from "./modals/SenderProfileModal";
import ActivityMetadataModal from "./modals/ActivityMetadataModal";

interface ActivityVisitingCardProps {
  activityMetadata: any;
  onStartActivity: () => void;
  onDismiss?: () => void;
  position: { x: number; y: number };
}

const ActivityVisitingCard: React.FC<ActivityVisitingCardProps> = ({
  activityMetadata,
  onStartActivity,
  onDismiss,
  position,
}) => {
  const { t } = useTranslation();
  const [showClaryNextInfo, setShowClaryNextInfo] = useState(false);
  const [showActivityInfo, setShowActivityInfo] = useState(false);
  const [showSenderProfile, setShowSenderProfile] = useState(false);
  const [showActivityMetadata, setShowActivityMetadata] = useState(false);

  const activityName = activityMetadata?.name || "Activity";
  const author = activityMetadata?.fiUser;
  const sharerFullName =
    author?.displayName ||
    `${author?.firstName || ""} ${author?.lastName || ""}`.trim();
  const sharerEmail = author?.email;
  const sharerInitials =
    (author?.firstName?.[0] || "") + (author?.lastName?.[0] || "") ||
    (author?.displayName?.[0] || "") ||
    (author?.email?.[0]?.toUpperCase() || "?");

  // Draggable functionality for desktop
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: "visiting-card",
    });

  // Combine saved position with current drag transform
  const style = {
    transform: transform
      ? `translate3d(${position.x + transform.x}px, ${position.y + transform.y}px, 0)`
      : `translate3d(${position.x}px, ${position.y}px, 0)`,
  };

  return (
    <>
      {/* Semi-transparent backdrop - only visible on mobile */}
      <div className="fixed inset-0 bg-black/20 backdrop-blur-[2px] sm:bg-transparent sm:backdrop-blur-none sm:pointer-events-none" />

      {/* Visiting Card */}
      <div
        ref={setNodeRef}
        style={style}
        className={`fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 ${
          isDragging ? "cursor-grabbing" : ""
        }`}
      >
        <Card className="w-[calc(100vw-2rem)] sm:w-[420px] border-2 shadow-2xl bg-white/95 backdrop-blur-sm">
          {/* Drag Handle - Desktop only */}
          <div
            {...listeners}
            {...attributes}
            className="items-center justify-center hidden py-1 border-b sm:flex cursor-grab active:cursor-grabbing bg-gray-50/50"
          >
            <GripVertical className="w-4 h-4 text-gray-400" />
          </div>

          <CardContent className="p-5 space-y-4 sm:p-6">
            {/* Close Button */}
            <button
              onClick={onDismiss}
              className="absolute p-1 transition-colors rounded-full top-3 right-3 hover:bg-gray-100"
              aria-label="Close"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>

            {/* 1. Sender Information (Most Specific) */}
            <div className="flex items-start gap-3 pr-6">
              <Avatar className="flex-shrink-0 w-10 h-10 ring-2 ring-primary/20">
                <AvatarFallback className="text-sm font-semibold text-white bg-gradient-to-br from-primary to-purple-600">
                  {sharerInitials}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="mb-1 text-sm text-gray-600">
                  <button
                    onClick={() => setShowSenderProfile(true)}
                    className="font-semibold text-gray-900 underline transition-colors hover:text-primary decoration-dotted hover:decoration-solid"
                  >
                    {sharerFullName || sharerEmail || t("an_expert") || "An expert"}
                  </button>{" "}
                  {t("shared_this_with_you") || "shared this activity with you"}
                </p>
              </div>
            </div>

            {/* 2. Activity Name (Specific) */}
            <div className="pl-4 border-l-2 border-primary/30">
              <button
                onClick={() => setShowActivityMetadata(true)}
                className="w-full text-lg font-bold text-left text-gray-900 transition-colors hover:text-primary group"
              >
                <span className="underline decoration-dotted group-hover:decoration-solid decoration-2">
                  {activityName}
                </span>
              </button>
            </div>

            {/* 3. Activity Concept & Platform (General) */}
            <p className="text-xs leading-relaxed text-gray-600">
              {t("this_is_an") || "This is an"}{" "}
              <button
                onClick={() => setShowActivityInfo(true)}
                className="font-medium underline transition-colors text-primary hover:text-primary/80 decoration-dotted hover:decoration-solid"
              >
                {t("activity") || "activity"}
              </button>{" "}
              {t("brought_to_you_by") || "brought to you by"}{" "}
              <button
                onClick={() => setShowClaryNextInfo(true)}
                className="font-medium underline transition-colors text-primary hover:text-primary/80 decoration-dotted hover:decoration-solid"
              >
                ClaryNext
              </button>
              <span className="block mt-1 italic text-gray-500">
                {t("platform_tagline") || "actionable wisdom exchange platform"}
              </span>
            </p>

            {/* Action Button */}
            <Button
              onClick={onStartActivity}
              className="w-full font-semibold text-white transition-all shadow-lg bg-primary hover:bg-primary/90 hover:shadow-xl"
              size="lg"
            >
              {t("got_it") || "Got it"}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <ClaryNextInfoModal
        isOpen={showClaryNextInfo}
        onClose={() => setShowClaryNextInfo(false)}
      />
      <ActivityInfoModal
        isOpen={showActivityInfo}
        onClose={() => setShowActivityInfo(false)}
      />
      <SenderProfileModal
        isOpen={showSenderProfile}
        onClose={() => setShowSenderProfile(false)}
        sender={author}
      />
      <ActivityMetadataModal
        isOpen={showActivityMetadata}
        onClose={() => setShowActivityMetadata(false)}
        activityMetadata={activityMetadata}
      />
    </>
  );
};

export default ActivityVisitingCard;
